import { io } from'socket.io-client';
import { getApiUrl } from'./api';

let socket = null;
let connectionState ='disconnected';
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
let visibilityChangeHandler = null;
let onlineHandler = null;
let offlineHandler = null;

// Detect if running on mobile device
const isMobileDevice = () => {
 return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
 (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
};

// Detect if running in PWA mode
const isPWA = () => {
 return window.matchMedia('(display-mode: standalone)').matches ||
 window.navigator.standalone === true ||
 document.referrer.includes('android-app://');
};

const cleanupLifecycleListeners = () => {
 if (typeof document !=='undefined'&& visibilityChangeHandler) {
 document.removeEventListener('visibilitychange', visibilityChangeHandler);
 visibilityChangeHandler = null;
 }

 if (typeof window !=='undefined'&& onlineHandler) {
 window.removeEventListener('online', onlineHandler);
 onlineHandler = null;
 }

 if (typeof window !=='undefined'&& offlineHandler) {
 window.removeEventListener('offline', offlineHandler);
 offlineHandler = null;
 }
};

const getSocket = () => {
 if (!socket) {
 const token = localStorage.getItem('uttaapp_token');
 if (!token) {
 console.warn('⚠️ No authentication token found. Socket connection will fail.');
 return null;
 }

 // Get API URL using helper function
 const apiUrl = getApiUrl();
 
 const isMobile = isMobileDevice();
 const isStandalone = isPWA();

 console.log(`🔌 Initializing Socket.IO connection...`);
 console.log(`📱 Device: ${isMobile ?'Mobile':'Desktop'}`);
 console.log(`📲 PWA Mode: ${isStandalone ?'Yes':'No'}`);
 console.log(`🌐 API URL: ${apiUrl}`);

 // Enhanced configuration for production HTTPS/WebSocket support
 // CRITICAL: Use WebSocket first for production (better performance, lower latency)
 // Fallback to polling if WebSocket upgrade fails
 const isProduction = apiUrl.includes('https://');
 
 socket = io(apiUrl, {
 auth: {
 token: token
 },
 // CRITICAL: WebSocket first for production HTTPS, polling fallback
 // Mobile devices can still use WebSocket, but polling is more reliable on poor networks
 transports: isProduction 
 ? ['websocket','polling'] // Production: WebSocket first for HTTPS
 : (isMobile ? ['polling','websocket'] : ['websocket','polling']), // Dev: Mobile prefers polling
 // Enhanced reconnection settings
 reconnection: true,
 reconnectionDelay: isMobile ? 2000 : 1000,
 reconnectionDelayMax: isMobile ? 10000 : 5000,
 reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
 // Timeout settings optimized for production networks
 timeout: isProduction ? 20000 : (isMobile ? 20000 : 10000),
 // CRITICAL: Don't force new connection in production (reuse existing)
 forceNew: !isProduction && isMobile, // Only force new for mobile in dev
 // Upgrade timeout for WebSocket upgrade
 upgradeTimeout: isProduction ? 10000 : (isMobile ? 10000 : 5000),
 // Auto-connect
 autoConnect: true,
 // Enable multiplexing
 multiplex: false,
 // CRITICAL: Enable connection state recovery for production reliability
 withCredentials: true, // Required for CORS with credentials
 // CRITICAL: Enable compression for better performance
 compression: true
 });

 // Connection event handlers
 socket.on('connect', () => {
 connectionState ='connected';
 reconnectAttempts = 0;
 console.log('✅ Connected to Socket.IO server');
 console.log(`🔌 Socket ID: ${socket.id}`);
 console.log(`📡 Transport: ${socket.io.engine.transport.name}`);
 
 // Emit connection info for debugging
 socket.emit('client-info', {
 deviceType: isMobile ?'mobile':'desktop',
 isPWA: isStandalone,
 userAgent: navigator.userAgent,
 timestamp: new Date().toISOString()
 });

 // Check block status after connection
 socket.emit('check-block-status');
 });

 socket.on('disconnect', (reason) => {
 connectionState ='disconnected';
 console.log(`❌ Disconnected from Socket.IO server. Reason: ${reason}`);
 
 // Auto-reconnect on mobile if not intentional disconnect
 if (reason ==='io server disconnect') {
 // Server disconnected, reconnect manually
 console.log('🔄 Server disconnected. Reconnecting...');
 socket.connect();
 } else if (reason ==='transport close'|| reason ==='transport error') {
 // Network error, will auto-reconnect
 console.log('🔄 Network error. Will auto-reconnect...');
 }
 });

 socket.on('connect_error', (error) => {
 connectionState ='error';
 reconnectAttempts++;
 console.error('❌ Socket.IO connection error:', error.message);
 
 if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
 console.error('❌ Max reconnection attempts reached. Please refresh the page.');
 // Could emit a custom event here for UI to show reconnection message
 } else {
 console.log(`🔄 Reconnection attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`);
 }
 });

 socket.on('reconnect', (attemptNumber) => {
 connectionState ='connected';
 reconnectAttempts = 0;
 console.log(`✅ Reconnected to Socket.IO server after ${attemptNumber} attempts`);
 });

 socket.on('reconnect_attempt', (attemptNumber) => {
 console.log(`🔄 Reconnection attempt ${attemptNumber}/${MAX_RECONNECT_ATTEMPTS}`);
 });

 socket.on('reconnect_error', (error) => {
 console.error('❌ Reconnection error:', error.message);
 });

 socket.on('reconnect_failed', () => {
 connectionState ='error';
 console.error('❌ Reconnection failed. Max attempts reached.');
 });

 // Handle ping/pong for connection health
 socket.on('ping', () => {
 socket.emit('pong');
 });


 // Listen for user blocked/unblocked events - set up immediately
 socket.on('user-blocked', (data) => {
 console.log('🚫 User blocked notification received in socket.js:', data);
 // Emit custom event for App.js to handle
 const eventData = {
 blocked: true,
 reason: data.reason || data.message ||'Your account has been blocked',
 blockedAt: data.blockedAt,
 ...data
 };
 window.dispatchEvent(new CustomEvent('user-blocked', { detail: eventData }));
 });

 socket.on('user-unblocked', (data) => {
 console.log('✅ User unblocked notification received in socket.js:', data);
 window.dispatchEvent(new CustomEvent('user-unblocked', { detail: data }));
 });

 // Listen for user deleted event - set up immediately
 socket.on('user-deleted', (data) => {
 console.log('🗑️ User deleted notification received in socket.js:', data);
 // Emit custom event for App.js to handle
 const eventData = {
 deleted: true,
 message: data.message ||'Your account has been deleted by the administrator.',
 deletedAt: data.deletedAt,
 ...data
 };
 window.dispatchEvent(new CustomEvent('user-deleted', { detail: eventData }));
 });

 socket.on('new-notification', (notification) => {
 // Check if it's a blocking notification
 if (notification.type ==='user_blocked'|| notification.blocked) {
 console.log('🚫 User blocked via notification:', notification);
 const eventData = {
 blocked: true,
 reason: notification.reason || notification.message ||'Your account has been blocked',
 ...notification
 };
 window.dispatchEvent(new CustomEvent('user-blocked', { detail: eventData }));
 } else if (notification.type ==='user_unblocked'|| notification.blocked === false) {
 console.log('✅ User unblocked via notification:', notification);
 window.dispatchEvent(new CustomEvent('user-unblocked', { detail: notification }));
 } else if (notification.type ==='account_deleted'|| notification.deleted) {
 console.log('🗑️ User deleted via notification:', notification);
 const eventData = {
 deleted: true,
 message: notification.message ||'Your account has been deleted by the administrator.',
 ...notification
 };
 window.dispatchEvent(new CustomEvent('user-deleted', { detail: eventData }));
 }
 });

 // Mobile-specific: Handle visibility changes (app goes to background/foreground)
 if (typeof document !=='undefined') {
 cleanupLifecycleListeners();

 visibilityChangeHandler = () => {
 if (document.hidden) {
 console.log('📱 App went to background');
 // Keep connection alive but reduce activity
 } else {
 console.log('📱 App came to foreground');
 // Ensure connection is active
 if (!socket.connected) {
 console.log('🔄 Reconnecting after app came to foreground...');
 socket.connect();
 }
 }
 };

 document.addEventListener('visibilitychange', visibilityChangeHandler);

 // Mobile-specific: Handle online/offline events
 onlineHandler = () => {
 console.log('🌐 Device came online');
 if (!socket.connected) {
 console.log('🔄 Reconnecting after coming online...');
 socket.connect();
 }
 };

 offlineHandler = () => {
 console.log('📴 Device went offline');
 // Connection will be lost, will auto-reconnect when back online
 };

 window.addEventListener('online', onlineHandler);
 window.addEventListener('offline', offlineHandler);
 }
 }

 return socket;
};

const disconnectSocket = () => {
 if (socket) {
 cleanupLifecycleListeners();
 socket.disconnect();
 socket = null;
 connectionState ='disconnected';
 reconnectAttempts = 0;
 console.log('🔌 Socket.IO disconnected');
 }
};

// Get current connection state
const getConnectionState = () => {
 if (!socket) return'disconnected';
 return socket.connected ?'connected': connectionState;
};

// Check if socket is connected
const isConnected = () => {
 return socket && socket.connected;
};

// Force reconnection (useful for mobile network switching)
const reconnectSocket = () => {
 if (socket) {
 console.log('🔄 Forcing socket reconnection...');
 socket.disconnect();
 socket.connect();
 } else {
 getSocket();
 }
};

export { getSocket, disconnectSocket, getConnectionState, isConnected, reconnectSocket, isMobileDevice, isPWA };

